import { Suspense, lazy } from "react";
import { Outlet, createBrowserRouter, useLocation } from "react-router-dom";
import { Landing } from "./screens/landing";
import { useSession } from "./lib/session";
import { useConfig } from "./lib/config";
import { useTelemetry, usePageViews } from "./lib/telemetry";
import { useHead } from "./lib/head";
import { headFor } from "./lib/head-specs";
import { CookieBox } from "./ui/CookieBox";

/* ------------------------------------------------------------ the split
 *
 * Every screen except the landing is loaded on demand.
 *
 * The landing is the one screen a stranger arrives on, and until 7 September
 * 2026 arriving on it downloaded all twenty six: 578 kB of JavaScript, most of
 * it the report, the admin queue and the capture flow, none of which a person
 * reading the front page will ever open. Rollup splits at every `import()`, so
 * each of these is its own chunk and the first script a phone downloads is the
 * landing and the router.
 *
 * `.then(m => ({ default: m.X }))` because every screen in this app is a named
 * export and `React.lazy` wants a default. Two screens come out of one module
 * where the module is one flow: the two auth screens, the three onboarding
 * ones, the two capture ones, the report and its confirm step.
 */
const HowItWorks = lazy(async () => ({
  default: (await import("./screens/marketing/HowItWorks")).HowItWorks }));
const Pricing = lazy(async () => ({
  default: (await import("./screens/marketing/Pricing")).Pricing }));
const Privacy = lazy(async () => ({
  default: (await import("./screens/marketing/Privacy")).Privacy }));
const Sources = lazy(async () => ({
  default: (await import("./screens/marketing/Sources")).Sources }));
const Sample = lazy(async () => ({
  default: (await import("./screens/landing/SampleReport")).Sample }));
const NotFound = lazy(async () => ({
  default: (await import("./screens/landing/NotFound")).NotFound }));
const SignIn = lazy(async () => ({ default: (await import("./screens/auth")).SignIn }));
const SignInCode = lazy(async () => ({
  default: (await import("./screens/auth")).SignInCode }));
const Consent = lazy(async () => ({
  default: (await import("./screens/onboarding")).Consent }));
const AddChild = lazy(async () => ({
  default: (await import("./screens/onboarding")).AddChild }));
const RestoreChildren = lazy(async () => ({
  default: (await import("./screens/onboarding")).RestoreChildren }));
const Home = lazy(async () => ({ default: (await import("./screens/home")).Home }));
const Pages = lazy(async () => ({ default: (await import("./screens/pages")).Pages }));
const Paper = lazy(async () => ({ default: (await import("./screens/pages")).Paper }));
const Account = lazy(async () => ({ default: (await import("./screens/account")).Account }));
const Capture = lazy(async () => ({ default: (await import("./screens/capture")).Capture }));
const Preview = lazy(async () => ({ default: (await import("./screens/capture")).Preview }));
const Reading = lazy(async () => ({ default: (await import("./screens/reading")).Reading }));
const Result = lazy(async () => ({ default: (await import("./screens/result")).Result }));
const Confirm = lazy(async () => ({ default: (await import("./screens/result")).Confirm }));
const Practice = lazy(async () => ({
  default: (await import("./screens/practice")).Practice }));
const OakPractice = lazy(async () => ({
  default: (await import("./screens/practice/OakPractice")).OakPractice }));
const Typed = lazy(async () => ({ default: (await import("./screens/typed")).Typed }));
const Credits = lazy(async () => ({ default: (await import("./screens/credits")).Credits }));
const Admin = lazy(async () => ({ default: (await import("./screens/admin")).Admin }));

/**
 * A signed-in parent stays in the app, including before adding a first child.
 * The session hook updates this route when sign-in changes in this or another tab.
 */
function Root() {
  const { token } = useSession();
  return token ? <Home /> : <Landing />;
}

/* ------------------------------------------------------ Agent V5-C, wave 2
 *
 * One layout above every route, because four things belong to the app rather
 * than to any screen: the host's own switches, the cookie box, the page views,
 * and what the document tells a crawler about the route on screen.
 * `useConfig` asks `GET /v1/config` once per launch whatever screen the parent
 * landed on, and every screen that needs an answer reads the same store rather
 * than asking again.
 *
 * The head is set here rather than in each screen for the reason given at the
 * top of `lib/head-specs.ts`: a route added below and forgotten in the table
 * still comes out `noindex, nofollow`, which is the safe answer for a screen
 * that shows a child's work.
 *
 * It renders no chrome of its own. Each screen still draws its own `Shell`,
 * so nothing about the layout of any screen changes.
 */
function Launch() {
  const cfg = useConfig();
  const { pathname } = useLocation();
  useTelemetry(cfg);
  usePageViews(pathname);
  useHead(headFor(pathname));
  return (
    <>
      {/* No spinner. A chunk on a warm cache is one frame, and a spinner that
          flashes for one frame is worse than nothing at all. The screens that
          wait on the network draw their own skeletons. */}
      <Suspense fallback={null}>
        <Outlet />
      </Suspense>
      <CookieBox />
    </>
  );
}

export const router = createBrowserRouter([
  {
    element: <Launch />,
    children: [
      { path: "/", element: <Root /> },
      { path: "/how-it-works", element: <HowItWorks /> },
      { path: "/sample", element: <Sample /> },
      { path: "/pricing", element: <Pricing /> },
      { path: "/privacy", element: <Privacy /> },
      { path: "/sources", element: <Sources /> },
      { path: "/signin", element: <SignIn /> },
      { path: "/signin/code", element: <SignInCode /> },
      { path: "/consent", element: <Consent /> },
      { path: "/children/new", element: <AddChild /> },
      { path: "/children/restore", element: <RestoreChildren /> },
      { path: "/home", element: <Home /> },
      { path: "/capture", element: <Capture /> },
      { path: "/preview", element: <Preview /> },
      { path: "/type", element: <Typed /> },
      { path: "/reading/:job", element: <Reading /> },
      { path: "/page/:sha", element: <Result /> },
      { path: "/page/:sha/confirm", element: <Confirm /> },
      { path: "/practice/:sha", element: <Practice /> },
      { path: "/resources/:slug", element: <OakPractice /> },
      { path: "/objectives/:slug/practice", element: <OakPractice syllabus /> },
      { path: "/pages", element: <Pages /> },
      { path: "/paper/:paper", element: <Paper /> },
      { path: "/account", element: <Account /> },
      { path: "/credits", element: <Credits /> },
      /* Agent V5-C, wave 2. Not in the tab bar: it is reached from Account,
         and only an account the service calls an admin sees it there. */
      { path: "/admin", element: <Admin /> },
      { path: "*", element: <NotFound /> },
    ],
  },
]);
